import { getNiceButtonStyle } from '@upright/ui/common/generic/components/buttons/nice-button';
import { getBouLinkStyle } from '@upright/ui/common/generic/content/texts/bou-link';
import { getTitleStyle } from '@upright/ui/common/generic/content/texts/bou-title/title-style';
import { BouColor } from '@upright/ui/common/generic/global/bou-colors';
import { mobileOrNarrower, mobileOrWider } from '@upright/ui/common/generic/layout/breakpoint-utils';
import { FormModal } from '@upright/ui/common/generic/modals/form-modal';
import { useEffect } from 'react';
import styled, { css } from 'styled-components';
import { SubscriptionTier } from '../../../../../common/activities/contact-forms/company-subscription/subscription-tiers';
import { useGetTimeoutRef } from '../../../generic/hooks/use-get-timeout-ref';
import { SubscriptionContextProvider, useSubscriptionContextBuilder } from './context';
import { CompanySubscriptionContactForm } from './subscribe-page/contact-form';
import { FreeTierSubscriptionPage } from './subscribe-page/free-tier';
import { SubscriptionTiersModalPage } from './subscription-tiers-page';
import { emptySubscriptionParams } from './types';

export const CompanySubscriptionModal = () => {
  const context = useSubscriptionContextBuilder();
  const [params, setParams] = context.params;
  const [modalOpen, setModalOpen] = context.modalOpen;

  const modalId = 'subscription-modal';

  useEffect(() => {
    const modalParent = document.querySelector(`#${modalId}-content`) as HTMLElement | undefined;
    if (modalParent) modalParent.scrollTo(0, 0);
  }, [params.tier, params.success, params.subscribe]);

  const timeoutRef = useGetTimeoutRef();

  return (
    <SubscriptionContextProvider value={context}>
      <StyledFormModal
        className="subscription-form-modal"
        {...{
          isOpen: modalOpen,
          closeModal: () => {
            setModalOpen(false);
            // A minor timeout before resetting, in order to give the modal some time to fade away
            // without its content changing.
            timeoutRef.current = setTimeout(() => {
              context.reset();
              setParams(emptySubscriptionParams);
            }, 500);
          },
          accessibleName: 'Subscribe to Upright platform',
          modalId: 'subscription-modal',
          exit: context.submitQuery.status === 'success' ? 'down' : 'up',
        }}
      >
        <Form>
          {(() => {
            switch (params.tier) {
              case SubscriptionTier.FREE:
                return <FreeTierSubscriptionPage />;
              case SubscriptionTier.STANDARD:
              case SubscriptionTier.ADVANCED:
                return <CompanySubscriptionContactForm />;
              case SubscriptionTier.DEMO:
                return <CompanySubscriptionContactForm hideBackLink />;

              default:
                return <SubscriptionTiersModalPage />;
            }
          })()}
        </Form>
      </StyledFormModal>
    </SubscriptionContextProvider>
  );
};

const StyledFormModal = styled(FormModal)`
  /* Overrides the default hidden due to removal of FormModalContent's max-height (.subscription-tiers-modal-content below).
  This ensures scrolling when if the modal doesn't have enough space vertically. */
  .ReactModal__Content {
    overflow-y: scroll;
  }
`;

const Form = styled.div`
  /* Everything below will essentially apply to all the "Root" elements of the individual forms */
  h1 {
    ${getTitleStyle({ size: 'h5' })}
    ${mobileOrNarrower(css`
      ${getTitleStyle({ size: 'h4' })}
    `)}
  }

  div.subscriptionForm {
    margin: 2em 0;
    display: flex;
    flex-direction: column;
    justify-content: space-between;

    p + p {
      margin-top: 1em;
    }

    section + section {
      margin-top: 2em;
    }

    section#inputs {
      > * + * {
        margin-top: 1em;
      }
    }

    span#contactLink {
      ${getBouLinkStyle()}
      font-weight: 600;
    }

    div.success {
      margin: 0 auto;
      width: 90%;
      max-width: 480px;
      display: flex;
      flex-direction: column;
      justify-content: center;
      align-items: center;
      text-align: center;
      > * + * {
        margin-top: 1.5em;
      }
      ${mobileOrNarrower(css`
        margin-top: 10vh;
      `)}
    }

    div.subscriptionError {
      color: ${BouColor.TOOL_TEXT_ERROR_RED};
    }
  }
  section#submit {
    button {
      ${getNiceButtonStyle({ theme: 'dark' })}
      width: 100%;
      ${mobileOrWider(css`
        max-width: 150px;
      `)}
    }
  }
`;
